import React from 'react';
import { Helmet } from 'react-helmet';
import style from './Schools.module.scss';

const STEPS = [
    {
        title: 'Discovery call',
        body: 'We start with your teacher to discuss the class, learning goals, available equipment and timetable. Together, we shape a program suited to your students.',
    },
    {
        title: 'Identify five community stories',
        body: 'We help students investigate local history and choose five stories to explore. Families, historical societies, museums and community members can contribute ideas, photographs and memories.',
    },
    {
        title: 'Filmmaking workshops',
        body: 'Practical workshops introduce storytelling, camera skills, framing, lighting and sound. Students work in small teams to plan their films, prepare interview questions and organise the shots they need.',
    },
    {
        title: 'Interview local people',
        body: 'Students meet and interview people connected to their stories, developing listening skills, confidence and respect for different perspectives. We guide thoughtful interviewing and work with the school to arrange permissions. Stories involving Aboriginal and Torres Strait Islander knowledge are developed with the appropriate community guidance.',
    },
    {
        title: 'Edit and bring the stories together',
        body: 'Students learn to select interview excerpts, combine footage and photographs, and add titles, sound and credits. They discover how editing shapes a clear, engaging story while checking facts and representing contributors respectfully.',
    },
    {
        title: "Share your school's films on Yesterday Stories",
        body: "With school approval and the necessary permissions, the finished films are added to the Yesterday Stories app, credited to your school and linked to the places where the stories happened. Families, residents and visitors can discover the students' work.",
    },
];

const Schools = () => (
    <>
        <Helmet>
            <title>Schools - Yesterday Stories</title>
            <meta name="description" content="School filmmaking workshops that bring local history to life. Yesterday Stories works with one class to discover five local stories and turn them into short documentary films." />
            <meta name="robots" content="index, follow" />
        </Helmet>

        <section className={style.hero}>
            <div className={`wrap ${style.heroInner}`}>
                <div>
                    <div className="eyebrow">For schools</div>
                    <h1 className={style.title}>Your class. Five local stories. A lasting community legacy.</h1>
                    <p className={style.subtitle}>School filmmaking workshops that bring local history to life.</p>
                    <p className={style.body}>
                        Yesterday Stories works with one class at your school to discover five local stories and turn
                        them into short documentary films. Students become researchers, interviewers and filmmakers,
                        learning from the people and places around them.
                    </p>
                    <div className={style.actions}>
                        <a href="mailto:info@yesterdaystories.com.au" className={style.btnPrimary}>Contact us</a>
                        <a href="#how-it-works-schools" className={style.btnGhost}>How it works</a>
                    </div>
                </div>
                <div className={style.heroFrame}>
                    <img src="/images/category/schools/school-kids-filming.png" alt="Students filmmaking in their community" />
                </div>
            </div>
        </section>

        <section className={style.intro}>
            <div className={`wrap ${style.introGrid}`}>
                <h2 className={style.introLead}>Every community has stories worth sharing.</h2>
                <div className={style.introText}>
                    <p>
                        From a much-loved local landmark to a family&rsquo;s migration journey, a community achievement
                        or a changing industry, these stories help students connect classroom learning with their own
                        neighbourhood.
                    </p>
                    <p>
                        Students become researchers, interviewers and filmmakers — learning from the people and places
                        around them, and leaving behind a resource future classes can enjoy.
                    </p>
                </div>
            </div>
        </section>

        <section id="how-it-works-schools" className={style.howItWorks}>
            <div className="wrap">
                <div className="section-head">
                    <div className="eyebrow">How it works</div>
                    <h2>Six steps, from idea to premiere.</h2>
                </div>
                <div className={style.stepsList}>
                    {STEPS.map((step, i) => (
                        <div className={style.stepRow} key={step.title}>
                            <div className={style.stepNumber}>{String(i + 1).padStart(2, '0')}</div>
                            <div>
                                <h3>{step.title}</h3>
                                <p>{step.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section className={style.legacy}>
            <div className={`wrap ${style.legacyInner}`}>
                <div className="eyebrow">Learning that leaves something behind</div>
                <h2>A resource future classes can enjoy.</h2>
                <p>
                    The program supports historical inquiry, literacy, digital skills, teamwork and creative
                    expression. It gives students an opportunity to contribute to their community while creating a
                    resource that future classes can enjoy.
                </p>
                <div className={style.legacyDivider} />
                <p className={style.legacyTagline}>One class can help preserve five stories for generations to come.</p>
            </div>
        </section>

        <section className={style.ctaSection}>
            <div className="wrap">
                <div className={style.ctaCard}>
                    <div>
                        <div className="eyebrow" style={{ color: 'var(--ink-deep)' }}>Get started</div>
                        <h2>Let&rsquo;s tell your community&rsquo;s stories.</h2>
                        <p>Contact us to discuss workshop options, timing and pricing.</p>
                        <a href="mailto:info@yesterdaystories.com.au" className={style.ctaBtn}>Contact us</a>
                        <div className={style.ctaLinks}>
                            <a href="mailto:info@yesterdaystories.com.au">info@yesterdaystories.com.au</a>
                            <a href="https://www.yesterdaystories.com" target="_blank" rel="noopener noreferrer">www.yesterdaystories.com</a>
                        </div>
                    </div>
                    <div className={style.ctaVisual}>
                        <img src="/images/category/schools/school-kids-filming.png" alt="Students on a Yesterday Stories filmmaking workshop" />
                    </div>
                </div>
            </div>
        </section>
    </>
);

export default Schools;
